import { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { Loader2, Upload, Download, CheckCircle2, RotateCcw, X, AlertCircle, FileSpreadsheet, ChevronDown, Archive } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";
import SelectorEmpleados from "./SelectorEmpleados";
import { useTurnos, filtrarEmpleados, idsACargar, SelectTurno } from "./FiltroTurnoObra";   // Fix FC

const inputCls = "w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white";
const labelCls = "block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1";

const TARGET_LABELS = {
  nombre: "Nombre", cedula: "Cédula", fecha: "Fecha",
  tramo1_hora_ingreso: "Hor. Ent 1", tramo1_hora_salida: "Hor. Sal 1",
  tramo2_hora_ingreso: "Hor. Ent 2", tramo2_hora_salida: "Hor. Sal 2",
  descanso: "Descanso",
};

// ─── MODAL: Importar desde marcador ────────────────────────────────────────
function ImportMarcadorModal({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [step, setStep] = useState("upload"); // upload | mapping | preview
  const [headers, setHeaders] = useState([]);
  const [mapping, setMapping] = useState({});
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const reset = () => { setFile(null); setStep("upload"); setHeaders([]); setMapping({}); setPreview(null); };
  const handleClose = () => { reset(); onClose(); };

  const doPreview = async (mapOverride) => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (mapOverride) fd.append("column_mapping", JSON.stringify(mapOverride));
      const res = await axios.post(`${API}/hr/parte-diario/admin/import/preview`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      if (res.data.needs_mapping) {
        setHeaders(res.data.headers || []);
        setMapping(res.data.auto_mapping || {});
        setStep("mapping");
      } else {
        setPreview(res.data);
        setStep("preview");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Error al leer el archivo"); }
    finally { setLoading(false); }
  };

  const confirmMapping = () => {
    if (!mapping.fecha || !(mapping.nombre !== undefined || mapping.cedula !== undefined)) {
      toast.error("Mapeá al menos Fecha y (Nombre o Cédula)"); return;
    }
    doPreview(mapping);
  };

  const handleConfirmImport = async () => {
    if (!preview?.valid_rows?.length) { toast.error("No hay filas válidas para importar"); return; }
    setLoading(true);
    try {
      const rows = preview.valid_rows.map(r => ({
        worker_id: r.worker_id, date: r.date,
        tramo1_hora_ingreso: r.tramo1_hora_ingreso, tramo1_hora_salida: r.tramo1_hora_salida,
        tramo2_hora_ingreso: r.tramo2_hora_ingreso, tramo2_hora_salida: r.tramo2_hora_salida,
        descanso: r.descanso || "",
        horas_trabajadas: r.horas_trabajadas,   // Fix EP: las del reloj cuando vienen en la planilla
      }));
      const res = await axios.post(`${API}/hr/parte-diario/admin/import/confirm`, { rows }, { withCredentials: true });
      toast.success(`Importado: ${res.data.inserted} nuevos, ${res.data.updated} actualizados${res.data.skipped_validated ? `, ${res.data.skipped_validated} omitidos (ya validados)` : ""}`);
      onDone();
      handleClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al confirmar importación"); }
    finally { setLoading(false); }
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="import-marcador-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-marcador-modal">
        <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-100 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-base font-bold text-gray-900">Importar desde Marcador</h2>
          <button onClick={handleClose} className="p-2 rounded-lg hover:bg-gray-100" data-testid="import-marcador-close"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          {step === "upload" && (
            <>
              <p className="text-sm text-gray-500">Subí el archivo del reloj/marcador (Excel o CSV). El sistema intentará detectar las columnas automáticamente.</p>
              {/* Fix EP (2026-09-30) */}
              <p className="text-xs text-gray-400" data-testid="import-marcador-formatos">Reconoce el «Reporte de ubicación mensual de marcaciones» y la «Tabla de Marcaciones - Genérico» (una hoja por trabajador): carga las marcas y las horas de cada persona y día.</p>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={e => setFile(e.target.files?.[0] || null)} className={inputCls} data-testid="import-marcador-file-input" />
              <button onClick={() => doPreview(null)} disabled={!file || loading} className="w-full bg-[#2E6DA4] hover:bg-[#245A8A] disabled:opacity-50 text-white rounded-xl py-2.5 font-semibold text-sm flex items-center justify-center gap-2" data-testid="import-marcador-analyze-btn">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Analizar Archivo
              </button>
            </>
          )}
          {step === "mapping" && (
            <>
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700 flex items-start gap-2">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                No se pudieron detectar todas las columnas automáticamente. Mapeá cada campo con la columna correspondiente del archivo.
              </div>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(TARGET_LABELS).map(([target, label]) => (
                  <div key={target}>
                    <label className={labelCls}>{label}{(target === "fecha") && " *"}</label>
                    <select value={mapping[target] ?? ""} onChange={e => setMapping(m => ({ ...m, [target]: e.target.value === "" ? undefined : Number(e.target.value) }))} className={inputCls} data-testid={`import-mapping-${target}`}>
                      <option value="">— sin mapear —</option>
                      {headers.map((h, idx) => <option key={idx} value={idx}>{h || `Columna ${idx + 1}`}</option>)}
                    </select>
                  </div>
                ))}
              </div>
              <button onClick={confirmMapping} disabled={loading} className="w-full bg-[#2E6DA4] hover:bg-[#245A8A] disabled:opacity-50 text-white rounded-xl py-2.5 font-semibold text-sm" data-testid="import-mapping-confirm-btn">
                {loading ? <Loader2 size={16} className="animate-spin inline" /> : "Continuar con este mapeo"}
              </button>
            </>
          )}
          {step === "preview" && preview && (
            <>
              <div className="flex gap-3 text-xs">
                {preview.formato && <span className="bg-blue-50 text-[#2E6DA4] px-2 py-1 rounded-full font-semibold" data-testid="import-marcador-formato">{preview.formato}</span>}
                <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full font-semibold">{preview.summary.valid} filas válidas</span>
                {preview.summary.errors > 0 && <span className="bg-red-100 text-red-600 px-2 py-1 rounded-full font-semibold">{preview.summary.errors} errores</span>}
              </div>
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-xl">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0"><tr>
                    <th className="px-2 py-1.5 text-left">Trabajador</th><th className="px-2 py-1.5">Fecha</th>
                    <th className="px-2 py-1.5">Ent1</th><th className="px-2 py-1.5">Sal1</th>
                    <th className="px-2 py-1.5">Ent2</th><th className="px-2 py-1.5">Sal2</th>
                    <th className="px-2 py-1.5">Descanso</th><th className="px-2 py-1.5">Horas</th>
                  </tr></thead>
                  <tbody>
                    {preview.valid_rows.map((r, i) => (
                      <tr key={i} className="border-t border-gray-100" data-testid={`import-preview-row-${i}`}>
                        <td className="px-2 py-1.5 font-medium">{r.worker_name}</td><td className="px-2 py-1.5 text-center">{r.date}</td>
                        <td className="px-2 py-1.5 text-center">{r.tramo1_hora_ingreso || "—"}</td><td className="px-2 py-1.5 text-center">{r.tramo1_hora_salida || "—"}</td>
                        <td className="px-2 py-1.5 text-center">{r.tramo2_hora_ingreso || "—"}</td><td className="px-2 py-1.5 text-center">{r.tramo2_hora_salida || "—"}</td>
                        <td className="px-2 py-1.5 text-center">{r.descanso || "—"}</td>
                        <td className="px-2 py-1.5 text-center font-bold">{r.horas_trabajadas}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.errors.length > 0 && (
                <div className="max-h-28 overflow-y-auto text-xs text-red-600 space-y-0.5">
                  {preview.errors.map((e, i) => <p key={i}>{e.message}</p>)}
                </div>
              )}
              <button onClick={handleConfirmImport} disabled={loading || !preview.valid_rows.length} className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl py-2.5 font-bold text-sm flex items-center justify-center gap-2" data-testid="import-marcador-confirm-btn">
                {loading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Confirmar Importación ({preview.valid_rows.length} filas)
              </button>
            </>
          )}
        </div>
      </div>
    </div>, document.body
  );
}

// ─── MODAL: Motivo de reapertura ───────────────────────────────────────────
function ReopenMotivoModal({ open, onClose, onConfirm, loading }) {
  const [motivo, setMotivo] = useState("");
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" data-testid="reopen-motivo-modal-overlay">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="reopen-motivo-modal">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-base font-bold text-gray-900">Reabrir período — Motivo</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-3">
          <textarea value={motivo} onChange={e => setMotivo(e.target.value)} rows={3} placeholder="Ej: corrección de horario mal cargado..." className={`${inputCls} text-sm`} data-testid="reopen-motivo-input" />
          <button onClick={() => onConfirm(motivo)} disabled={loading || !motivo.trim()} className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white rounded-xl py-2.5 font-bold text-sm flex items-center justify-center gap-2" data-testid="reopen-motivo-confirm-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />} Confirmar Reapertura
          </button>
        </div>
      </div>
    </div>, document.body
  );
}

// ─── PANEL: Partes Históricos (formato anterior, BLOQUE 57 PARTE C) ───────
// Solo lectura/exportación — el flujo de carga/edición pasó a Parte Diario
// Adm./Obra (Bloque 56). No borra ni modifica `daily_logs`; solo consulta y
// reexporta lo que ya existía, para no perder el acceso a esos datos.
function HistoricoParteDiarioPanel({ projects }) {
  const [open, setOpen] = useState(false);
  const [projectId, setProjectId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const activeProjects = (projects || []).filter(p => p.status === "activo");

  const loadLogs = async () => {
    if (!projectId) { toast.error("Seleccioná un proyecto"); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hr/daily-logs`, { params: { project_id: projectId, limit: 60 }, withCredentials: true });
      setLogs(res.data || []);
    } catch { toast.error("Error al consultar partes históricos"); }
    finally { setLoading(false); }
  };

  const handleExport = async () => {
    if (!projectId || !dateFrom || !dateTo) { toast.error("Seleccioná proyecto y rango de fechas"); return; }
    setExporting(true);
    try {
      const res = await axios.get(`${API}/hr/daily-logs/export-excel`, { params: { project_id: projectId, date_from: dateFrom, date_to: dateTo }, withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `Parte_Diario_Historico_${dateFrom}_al_${dateTo}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error al exportar"); }
    finally { setExporting(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden" data-testid="historico-parte-diario-panel">
      <button onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors" data-testid="historico-parte-toggle-btn">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <Archive size={15} className="text-gray-400" />
          Partes Históricos (formato anterior, solo consulta)
        </div>
        <ChevronDown size={15} className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-gray-100 pt-3 space-y-3">
          <p className="text-xs text-gray-500">Consultá o reexportá los partes diarios cargados con el formato anterior (1 tramo, por proyecto). Este panel es de solo lectura — el flujo actual para cargar horas es Parte Diario Adm./Obra.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="sm:col-span-1">
              <label className={labelCls}>Proyecto</label>
              <select value={projectId} onChange={e => setProjectId(e.target.value)} className={inputCls} data-testid="historico-parte-project-select">
                <option value="">Seleccionar...</option>
                {activeProjects.map(p => <option key={p._id || p.id} value={p._id || p.id}>{p.name}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Desde (para exportar)</label><input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={inputCls} data-testid="historico-parte-date-from" /></div>
            <div><label className={labelCls}>Hasta (para exportar)</label><input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className={inputCls} data-testid="historico-parte-date-to" /></div>
          </div>
          <div className="flex gap-2">
            <button onClick={loadLogs} disabled={loading} className="flex-1 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2" data-testid="historico-parte-load-btn">
              {loading ? <Loader2 size={15} className="animate-spin" /> : null} Consultar Últimos Partes
            </button>
            <button onClick={handleExport} disabled={exporting} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-xl px-4 py-2.5 text-sm font-semibold" data-testid="historico-parte-export-btn">
              {exporting ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />} Exportar Excel
            </button>
          </div>
          {logs && (
            <div className="border border-gray-200 rounded-xl overflow-hidden max-h-64 overflow-y-auto" data-testid="historico-parte-results">
              {logs.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-4">Sin partes históricos para este proyecto</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0"><tr>
                    <th className="px-2 py-1.5 text-left">Fecha</th><th className="px-2 py-1.5">Trabajadores</th><th className="px-2 py-1.5">Presentes</th>
                  </tr></thead>
                  <tbody>
                    {logs.map((l, i) => (
                      <tr key={l.id || l._id || i} className="border-t border-gray-100">
                        <td className="px-2 py-1.5">{l.date}</td>
                        <td className="px-2 py-1.5 text-center">{(l.entries || []).length}</td>
                        <td className="px-2 py-1.5 text-center">{(l.entries || []).filter(e => e.present).length}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Fix ES (2026-09-30): el día comparado con el turno del trabajador (solo informa).
function CompTurno({ c }) {
  if (!c) return <span className="text-[10px] text-gray-300">Sin turno</span>;
  const hm = m => `${Math.floor(m / 60)}:${String(m % 60).padStart(2, "0")}`;
  const chips = [];
  if (c.estado === "ausencia") chips.push(["Ausencia", "bg-red-100 text-red-700"]);
  if (c.estado === "libre") chips.push(["Libre", "bg-gray-100 text-gray-500"]);
  // Fix FI (2026-10-08): el feriado se marca en la fecha; acá, si lo trabajó.
  if (c.estado === "libre_trabajado") chips.push([c.feriado ? "Trabajó en feriado" : "Trabajó en día libre", "bg-violet-100 text-violet-700"]);
  if (c.estado === "sin_horario") chips.push(["Sin horario ese día", "bg-gray-100 text-gray-500"]);
  if (c.estado === "sin_salida") chips.push(["Sin salida", "bg-amber-100 text-amber-700"]);
  if (c.tarde_min) chips.push([`Tarde ${c.tarde_min} min`, "bg-amber-100 text-amber-700"]);
  if (c.salida_antes_min) chips.push([`Salió ${c.salida_antes_min} min antes`, "bg-amber-100 text-amber-700"]);
  if (c.receso_exceso_min) chips.push([`Receso +${c.receso_exceso_min} min`, "bg-amber-100 text-amber-700"]);
  if (c.he_min) chips.push([`HE ${hm(c.he_min)}`, "bg-blue-100 text-[#2E6DA4]"]);
  // Fix GN (2026-10-16): jornal de sábados, domingos y feriados, y penalizaciones del turno.
  if (c.jornal === "completo") chips.push(["Jornal completo", "bg-violet-100 text-violet-700"]);
  if (c.jornal === "medio") chips.push(["Medio jornal", "bg-violet-100 text-violet-700"]);
  if (c.pen_min) chips.push([`Penalización −${hm(c.pen_min)}`, "bg-red-100 text-red-700"]);
  if (c.estado === "ok" && !c.he_min) chips.push(["En horario", "bg-emerald-100 text-emerald-700"]);
  return (
    <div className="flex flex-wrap gap-1" title={`${c.turno}${c.horario ? ` · ${c.horario}` : ""}${c.entrada ? ` · ${c.entrada}–${c.salida}` : ""}`}>
      {chips.map(([t, cls]) => <span key={t} className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${cls}`}>{t}</span>)}
    </div>
  );
}

// Fix FY (2026-10-14): "de las 9hs trabajadas, 8hs son normales, y 1hora es hora extra" (CEN).
// Las horas del día partidas en normales y extra (las HE que marca el turno), al minuto.
// Fix GA (2026-10-14): ahora la partida la arma el backend por tipo de hora (normal, nocturna, extra,
// extra nocturna, extra de feriado, domingo), con los factores y el redondeo del turno.
const TIPOS_PARTIDA = [["normal", "normales"], ["nocturna", "nocturnas"], ["extra", "extra"], ["extra_noct", "extra nocturnas"],
  ["extra_fer", "extra de feriado"], ["domingo", "de domingo"], ["feriado", "de feriado"]];   // Fix GN: jornal de feriado
function HorasPartidas({ horas, p, idx }) {
  const m = (p && p.min) || {};
  const total = Math.max(0, Math.round((Number(horas) || 0) * 60));
  const suma = Object.values(m).reduce((a, b) => a + b, 0);
  if (!total || !(Object.keys(m).some(k => k !== "normal" && m[k] > 0) || suma !== total)) return null;
  return (
    <p className="mt-1 text-[10px] font-semibold text-[#2E6DA4] whitespace-nowrap" data-testid={`admin-parte-partidas-${idx}`}>
      {TIPOS_PARTIDA.filter(([k]) => m[k] > 0).map(([k, l]) => `${(m[k] / 60).toFixed(2)}h ${l}`).join(" + ") || "0.00h"}
    </p>
  );
}

// Fix FI (2026-10-08): "poder marcar al momento de añadir las asistencias que días fueron
// feriados dentro del periodo seleccionado. Y en los días feriados automáticamente toma el
// criterio para esos días según el Turno asignado a cada personal" (CEN). Los feriados quedan
// en el calendario de la plataforma (vienen marcados los nacionales).
function FeriadosPeriodoModal({ open, desde, hasta, onClose, onSaved }) {
  const [marcados, setMarcados] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const dias = [];
  if (desde && hasta && desde <= hasta) {
    const d = new Date(`${desde}T12:00:00`);
    const fin = new Date(`${hasta}T12:00:00`);
    while (d <= fin && dias.length < 400) { dias.push(d.toISOString().slice(0, 10)); d.setDate(d.getDate() + 1); }
  }
  useEffect(() => {
    if (!open) return;
    setMarcados(null);
    axios.get(`${API}/calendario/feriados`, { withCredentials: true })
      .then(({ data }) => setMarcados(new Set((data.fechas || []).filter(f => f >= desde && f <= hasta))))
      .catch(() => { toast.error("No se pudieron traer los feriados"); setMarcados(new Set()); });
  }, [open, desde, hasta]);
  if (!open) return null;
  const toggle = (f) => setMarcados(prev => { const n = new Set(prev); n.has(f) ? n.delete(f) : n.add(f); return n; });
  const guardar = async () => {
    setGuardando(true);
    try {
      await axios.put(`${API}/calendario/feriados/periodo`, { desde, hasta, fechas: [...marcados] }, { withCredentials: true });
      toast.success("Feriados del período guardados");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudieron guardar los feriados"); }
    finally { setGuardando(false); }
  };
  const nombreDia = (f) => new Date(`${f}T12:00:00`).toLocaleDateString("es-PY", { weekday: "long", day: "2-digit", month: "2-digit" });
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col" data-testid="feriados-periodo-modal">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Feriados del período</h3>
            <p className="text-[11px] text-gray-500">Marcá los días que fueron feriados. Esos días se comparan con la fila «Feriado» del turno de cada trabajador.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
        </div>
        <div className="p-3 overflow-y-auto flex-1">
          {marcados === null ? <div className="flex items-center gap-2 text-xs text-gray-400 p-4"><Loader2 size={14} className="animate-spin" /> Cargando...</div> : (
            <div className="grid grid-cols-2 gap-1">
              {dias.map(f => (
                <label key={f} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs cursor-pointer ${marcados.has(f) ? "bg-violet-50 text-violet-800 font-semibold" : "hover:bg-gray-50 text-gray-600"}`}>
                  <input type="checkbox" checked={marcados.has(f)} onChange={() => toggle(f)} data-testid={`feriado-dia-${f}`} /> {nombreDia(f)}
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex gap-2">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2 text-sm font-medium">Cancelar</button>
          <button onClick={guardar} disabled={guardando || marcados === null} className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-xl py-2 text-sm font-semibold disabled:opacity-50" data-testid="feriados-periodo-guardar">
            {guardando ? "Guardando..." : `Guardar (${marcados ? marcados.size : 0})`}
          </button>
        </div>
      </div>
    </div>, document.body);
}

// Fix HC (2026-10-20): "al costado de «Feriados del periodo», tengamos otro botón de «Días de clima adverso»
// donde los días seleccionados entren dentro de la consideración" de la fila «Clima adverso» del turno: otras
// tolerancias y sin penalizaciones (CEN). Los días quedan en el calendario de la plataforma, como los feriados.
function ClimaAdversoModal({ open, desde, hasta, onClose, onSaved }) {
  const [marcados, setMarcados] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const dias = [];
  if (desde && hasta && desde <= hasta) {
    const d = new Date(`${desde}T12:00:00`);
    const fin = new Date(`${hasta}T12:00:00`);
    while (d <= fin && dias.length < 400) { dias.push(d.toISOString().slice(0, 10)); d.setDate(d.getDate() + 1); }
  }
  useEffect(() => {
    if (!open) return;
    setMarcados(null);
    axios.get(`${API}/calendario/clima-adverso`, { withCredentials: true })
      .then(({ data }) => setMarcados(new Set((data.fechas || []).filter(f => f >= desde && f <= hasta))))
      .catch(() => { toast.error("No se pudieron traer los días de clima adverso"); setMarcados(new Set()); });
  }, [open, desde, hasta]);
  if (!open) return null;
  const toggle = (f) => setMarcados(prev => { const n = new Set(prev); n.has(f) ? n.delete(f) : n.add(f); return n; });
  const guardar = async () => {
    setGuardando(true);
    try {
      await axios.put(`${API}/calendario/clima-adverso/periodo`, { desde, hasta, fechas: [...marcados] }, { withCredentials: true });
      toast.success("Días de clima adverso guardados");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudieron guardar los días de clima adverso"); }
    finally { setGuardando(false); }
  };
  const nombreDia = (f) => new Date(`${f}T12:00:00`).toLocaleDateString("es-PY", { weekday: "long", day: "2-digit", month: "2-digit" });
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[85vh] flex flex-col" data-testid="clima-periodo-modal">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Días de clima adverso</h3>
            <p className="text-[11px] text-gray-500">Marcá los días de clima adverso. Esos días se comparan con la fila «Clima adverso» del turno de cada trabajador y no aplican las penalizaciones.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100"><X size={16} /></button>
        </div>
        <div className="p-3 overflow-y-auto flex-1">
          {marcados === null ? <div className="flex items-center gap-2 text-xs text-gray-400 p-4"><Loader2 size={14} className="animate-spin" /> Cargando...</div> : (
            <div className="grid grid-cols-2 gap-1">
              {dias.map(f => (
                <label key={f} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs cursor-pointer ${marcados.has(f) ? "bg-sky-50 text-sky-800 font-semibold" : "hover:bg-gray-50 text-gray-600"}`}>
                  <input type="checkbox" checked={marcados.has(f)} onChange={() => toggle(f)} data-testid={`clima-dia-${f}`} /> {nombreDia(f)}
                </label>
              ))}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-100 flex gap-2">
          <button onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 rounded-xl py-2 text-sm font-medium">Cancelar</button>
          <button onClick={guardar} disabled={guardando || marcados === null} className="flex-1 bg-sky-600 hover:bg-sky-700 text-white rounded-xl py-2 text-sm font-semibold disabled:opacity-50" data-testid="clima-periodo-guardar">
            {guardando ? "Guardando..." : `Guardar (${marcados ? marcados.size : 0})`}
          </button>
        </div>
      </div>
    </div>, document.body);
}

// ─── TAB PRINCIPAL ──────────────────────────────────────────────────────────
export default function ParteDiarioAdminTab({ workers, projects }) {
  // Fix DW (2026-09-23): antes acá se filtraba por `status === "activo"`, así que con
  // la nómina en otro estado el desplegable quedaba vacío. Ahora se listan todos y el
  // propio desplegable muestra el estado de cada uno.
  const [workerIds, setWorkerIds] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [importOpen, setImportOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [feriadosOpen, setFeriadosOpen] = useState(false);   // Fix FI
  const [climaOpen, setClimaOpen] = useState(false);   // Fix HC
  const [busy, setBusy] = useState(false);
  // Fix FC (2026-10-06): filtros por turno y por obra.
  const turnos = useTurnos();
  const [turnoId, setTurnoId] = useState("");
  const [projectId, setProjectId] = useState("");
  const activeProjects = (projects || []).filter(p => p.status === "activo");

  const loadRecords = useCallback(async () => {
    const ids = idsACargar(workers, workerIds, turnoId, projectId);
    if (!ids.length || !dateFrom || !dateTo) { toast.error("Elegí empleados, un turno o una obra, y el rango de fechas"); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/hr/parte-diario/admin/records`, { params: { worker_ids: ids.join(","), date_from: dateFrom, date_to: dateTo }, withCredentials: true });
      setRecords(res.data);
      setSelected([]);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al cargar el período"); }
    finally { setLoading(false); }
  }, [workers, workerIds, turnoId, projectId, dateFrom, dateTo]);

  const calcHours = (i1, s1, i2, s2, descanso) => {
    const seg = (i, s) => {
      if (!i || !s) return 0;
      const [hi, mi] = i.split(":").map(Number); const [hs, ms] = s.split(":").map(Number);
      const mins = (hs * 60 + ms) - (hi * 60 + mi);
      return mins > 0 ? mins / 60 : 0;
    };
    const descansoHoras = descanso ? seg("00:00", descanso) : 0;
    return Math.round((seg(i1, s1) + seg(i2, s2) - descansoHoras) * 100) / 100;
  };

  const updateLocal = (idx, field, value) => {
    setRecords(prev => prev.map((r, i) => {
      if (i !== idx) return r;
      const next = { ...r, [field]: value };
      if (field.startsWith("tramo") || field === "descanso") {
        const newHoras = calcHours(next.tramo1_hora_ingreso, next.tramo1_hora_salida, next.tramo2_hora_ingreso, next.tramo2_hora_salida, next.descanso);
        if (newHoras < 0) {
          toast.error("El descanso no puede ser mayor a las horas trabajadas del día");
          return r;
        }
        next.horas_trabajadas = newHoras;
      }
      return next;
    }));
  };

  const saveRow = async (idx) => {
    const r = records[idx];
    setBusy(true);
    try {
      const res = await axios.put(`${API}/hr/parte-diario/admin/records/${r.id}`, {
        tramo1_hora_ingreso: r.tramo1_hora_ingreso, tramo1_hora_salida: r.tramo1_hora_salida,
        tramo2_hora_ingreso: r.tramo2_hora_ingreso, tramo2_hora_salida: r.tramo2_hora_salida,
        descanso: r.descanso || "",
        horas_trabajadas: r.horas_trabajadas,
      }, { withCredentials: true });
      setRecords(prev => prev.map((x, i) => i === idx ? res.data : x));
      toast.success("Fila guardada");
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setBusy(false); }
  };

  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelected(selected.length === records.length ? [] : records.map(r => r.id));

  const handleValidate = async () => {
    if (!selected.length) { toast.error("Seleccioná al menos un día"); return; }
    setBusy(true);
    try {
      const res = await axios.put(`${API}/hr/parte-diario/admin/validate`, { ids: selected }, { withCredentials: true });
      toast.success(`${res.data.validated_count} día(s) validado(s) — habilitado(s) para Parte Diario Obra`);
      loadRecords();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al validar"); }
    finally { setBusy(false); }
  };

  const handleReopen = async (motivo) => {
    setBusy(true);
    try {
      const res = await axios.put(`${API}/hr/parte-diario/admin/reopen`, { ids: selected, motivo }, { withCredentials: true });
      toast.success(`${res.data.reopened_count} día(s) reabierto(s)${res.data.obra_cascaded ? ` (${res.data.obra_cascaded} también revertido(s) en Parte Obra)` : ""}`);
      setReopenOpen(false);
      loadRecords();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al reabrir"); }
    finally { setBusy(false); }
  };

  const handleExport = async () => {
    if (!dateFrom || !dateTo) { toast.error("Definí el rango de fechas"); return; }
    try {
      const res = await axios.get(`${API}/hr/parte-diario/export-excel`, { params: { date_from: dateFrom, date_to: dateTo }, withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `Parte_Diario_${dateFrom}_al_${dateTo}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Error al exportar"); }
  };

  return (
    <div className="space-y-4 max-w-5xl mx-auto" data-testid="parte-diario-admin-tab">
      <div className="bg-white rounded-xl border border-gray-200/60 p-4 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-6 gap-3 mb-3">
          <div><label className={labelCls}>Turno</label>
            <SelectTurno turnos={turnos} value={turnoId} onChange={setTurnoId} className={inputCls} testid="admin-parte-turno-select" />
          </div>
          <div><label className={labelCls}>Obra</label>
            <select value={projectId} onChange={e => setProjectId(e.target.value)} className={inputCls} data-testid="admin-parte-project-select">
              <option value="">Todas</option>
              {activeProjects.map(p => <option key={p._id || p.id} value={p._id || p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Empleado</label>
            <SelectorEmpleados workers={filtrarEmpleados(workers, turnoId, projectId)} value={workerIds} onChange={setWorkerIds}
              testid="admin-parte-worker-select" placeholder={turnoId || projectId ? "Todos los del filtro" : "Seleccionar..."} />
          </div>
          <div><label className={labelCls}>Fecha Inicio</label><input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className={inputCls} data-testid="admin-parte-date-from" /></div>
          <div><label className={labelCls}>Fecha Fin</label><input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className={inputCls} data-testid="admin-parte-date-to" /></div>
        </div>
        <div className="flex gap-2">
          <button onClick={loadRecords} disabled={loading} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] disabled:opacity-50 text-white rounded-xl py-2.5 font-semibold text-sm flex items-center justify-center gap-2" data-testid="admin-parte-load-btn">
            {loading ? <Loader2 size={16} className="animate-spin" /> : null} Cargar Período
          </button>
          <button onClick={() => setImportOpen(true)} className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl px-4 py-2.5 text-sm font-semibold" data-testid="admin-parte-import-btn">
            <Upload size={15} /> Importar Marcador
          </button>
          <button onClick={handleExport} className="flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl px-4 py-2.5 text-sm font-semibold" data-testid="admin-parte-export-btn">
            <FileSpreadsheet size={15} /> Excel
          </button>
        </div>
      </div>

      {records.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 bg-gray-50/60">
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-600">
              <input type="checkbox" checked={selected.length === records.length && records.length > 0} onChange={toggleSelectAll} data-testid="admin-parte-select-all" />
              {selected.length} / {records.length} seleccionados
            </label>
            {/* Fix FI (2026-10-08): marcar los feriados del período. */}
            <div className="flex gap-2">
              <button onClick={() => setFeriadosOpen(true)} disabled={!dateFrom || !dateTo}
                className="flex items-center gap-1.5 border border-violet-200 text-violet-700 hover:bg-violet-50 disabled:opacity-40 rounded-lg px-3 py-1.5 text-xs font-bold" data-testid="admin-parte-feriados-btn">
                Feriados del período ({new Set(records.filter(r => r.feriado).map(r => r.date)).size})
              </button>
              {/* Fix HC (2026-10-20): marcar los días de clima adverso del período. */}
              <button onClick={() => setClimaOpen(true)} disabled={!dateFrom || !dateTo}
                className="flex items-center gap-1.5 border border-sky-200 text-sky-700 hover:bg-sky-50 disabled:opacity-40 rounded-lg px-3 py-1.5 text-xs font-bold" data-testid="admin-parte-clima-btn">
                Días de clima adverso ({new Set(records.filter(r => r.clima_adverso).map(r => r.date)).size})
              </button>
            </div>
            <div className="flex gap-2">
              <button onClick={handleValidate} disabled={busy || !selected.length} className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-lg px-3 py-1.5 text-xs font-bold" data-testid="admin-parte-validate-btn">
                <CheckCircle2 size={13} /> Validar Parte Diario Adm.
              </button>
              <button onClick={() => setReopenOpen(true)} disabled={busy || !selected.length} className="flex items-center gap-1.5 bg-amber-100 hover:bg-amber-200 disabled:opacity-40 text-amber-700 rounded-lg px-3 py-1.5 text-xs font-bold" data-testid="admin-parte-reopen-btn">
                <RotateCcw size={13} /> Reabrir
              </button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50"><tr>
                {/* Fix GI (2026-10-15): "en ambos partes diarios y liquidación … congelar las columnas de empleado y fecha" (CEN).
                    La casilla, el empleado y la fecha quedan fijos a la izquierda al desplazar la tabla. */}
                <th className="px-2 py-2 sticky left-0 z-20 bg-gray-50 w-10 min-w-[40px]"></th>
                <th className="px-2 py-2 text-left sticky left-[40px] z-20 bg-gray-50 w-[230px] min-w-[230px]">Empleado</th>
                <th className="px-2 py-2 text-left sticky left-[270px] z-20 bg-gray-50 w-[136px] min-w-[136px] shadow-[2px_0_4px_-2px_rgba(0,0,0,0.18)]">Fecha</th>
                <th className="px-2 py-2">Hor. Ent 1</th><th className="px-2 py-2">Hor. Sal 1</th>
                <th className="px-2 py-2">Hor. Ent 2</th><th className="px-2 py-2">Hor. Sal 2</th>
                <th className="px-2 py-2 text-amber-700">Descanso</th>
                <th className="px-2 py-2">Horas Trabajadas</th><th className="px-2 py-2 text-left">Turno</th><th className="px-2 py-2">Estado</th><th className="px-2 py-2"></th>
              </tr></thead>
              <tbody>
                {records.map((r, idx) => {
                  const locked = r.admin_status === "validado";
                  return (
                    <tr key={r.id} className="border-t border-gray-100" data-testid={`admin-parte-row-${idx}`}>
                      <td className="px-2 py-2 text-center sticky left-0 z-10 bg-white"><input type="checkbox" checked={selected.includes(r.id)} onChange={() => toggleSelect(r.id)} /></td>
                      <td className="px-2 py-2 whitespace-nowrap text-gray-700 sticky left-[40px] z-10 bg-white max-w-[230px] truncate" title={r.worker_name} data-testid={`admin-parte-worker-${idx}`}>{r.worker_name}</td>
                      <td className="px-2 py-2 font-medium whitespace-nowrap sticky left-[270px] z-10 bg-white shadow-[2px_0_4px_-2px_rgba(0,0,0,0.18)]">{r.date}
                        {r.feriado && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-violet-100 text-violet-700" data-testid={`admin-parte-feriado-${idx}`}>Feriado</span>}
                        {r.clima_adverso && !r.feriado && <span className="ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-sky-100 text-sky-700" data-testid={`admin-parte-clima-${idx}`}>Clima adverso</span>}
                      </td>
                      {["tramo1_hora_ingreso", "tramo1_hora_salida", "tramo2_hora_ingreso", "tramo2_hora_salida"].map(f => (
                        <td key={f} className="px-2 py-2">
                          <input type="time" disabled={locked} value={r[f] || ""} onChange={e => updateLocal(idx, f, e.target.value)} className="w-24 border border-gray-300 rounded-lg px-1.5 py-1 text-xs disabled:bg-gray-50 disabled:text-gray-400" data-testid={`admin-parte-${f}-${idx}`} />
                        </td>
                      ))}
                      <td className="px-2 py-2">
                        <input type="time" disabled={locked} value={r.descanso || ""} onChange={e => updateLocal(idx, "descanso", e.target.value)} className="w-24 border border-amber-200 bg-amber-50/40 rounded-lg px-1.5 py-1 text-xs disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-300" data-testid={`admin-parte-descanso-${idx}`} title="Tiempo de descanso del día (se resta de las Horas Trabajadas)" />
                      </td>
                      <td className="px-2 py-2">
                        <input type="number" step="0.01" disabled={locked} value={r.horas_trabajadas} onChange={e => updateLocal(idx, "horas_trabajadas", Number(e.target.value))} className="w-20 border border-gray-300 rounded-lg px-1.5 py-1 text-xs text-center font-bold text-[#2E6DA4] disabled:bg-gray-50 disabled:text-gray-400" data-testid={`admin-parte-horas-${idx}`} />
                        <HorasPartidas horas={r.horas_trabajadas} p={r.partida} idx={idx} />
                      </td>
                      <td className="px-2 py-2 min-w-[140px]" data-testid={`admin-parte-turno-${idx}`}><CompTurno c={r.comparacion} /></td>
                      <td className="px-2 py-2 text-center">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${locked ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{locked ? "Validado" : "Borrador"}</span>
                      </td>
                      <td className="px-2 py-2">
                        {!locked && <button onClick={() => saveRow(idx)} disabled={busy} className="text-[#2E6DA4] hover:underline font-semibold" data-testid={`admin-parte-save-${idx}`}>Guardar</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ImportMarcadorModal open={importOpen} onClose={() => setImportOpen(false)} onDone={loadRecords} />
      <ReopenMotivoModal open={reopenOpen} onClose={() => setReopenOpen(false)} onConfirm={handleReopen} loading={busy} />
      <FeriadosPeriodoModal open={feriadosOpen} desde={dateFrom} hasta={dateTo} onClose={() => setFeriadosOpen(false)}
        onSaved={() => { setFeriadosOpen(false); loadRecords(); }} />
      <ClimaAdversoModal open={climaOpen} desde={dateFrom} hasta={dateTo} onClose={() => setClimaOpen(false)}
        onSaved={() => { setClimaOpen(false); loadRecords(); }} />

      <HistoricoParteDiarioPanel projects={projects} />
    </div>
  );
}
